import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  LayoutTemplate, Plus, Search, Loader2, RefreshCw, Copy, Trash2, Edit2,
  Target, DollarSign, Clock, TrendingUp, Sparkles, Tag
} from 'lucide-react';
import { templateAPI } from '../api';
import { format } from 'date-fns';

const CATEGORIES = [
  { value: 'product_launch', label: 'Product Launch' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'evergreen', label: 'Evergreen' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'awareness', label: 'Brand Awareness' },
  { value: 'engagement', label: 'Engagement' }
];

const CHANNELS = [
  'email', 'social_media', 'ppc', 'content', 'influencer', 'events', 'seo', 'pr'
];

// Helper function to format category with proper capitalization (Bug #10)
const formatCategory = (category) => {
  if (!category) return '';
  // First check if it's in our CATEGORIES list
  const found = CATEGORIES.find(c => c.value === category);
  if (found) return found.label;
  // Otherwise, format the raw value: replace underscores with spaces and capitalize each word
  return category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

export const CampaignTemplatesPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUseDialog, setShowUseDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    campaign_type: '',
    channels: [],
    target_audience: '',
    default_headline: '',
    default_tagline: '',
    default_cta: '',
    default_body: '',
    default_budget: '',
    default_duration_days: '',
    category: '',
    tags: []
  });

  const [newCampaignName, setNewCampaignName] = useState('');
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, [searchQuery, filterCategory]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchQuery || undefined,
        category: filterCategory !== 'all' ? filterCategory : undefined
      };
      const res = await templateAPI.getAll(params);
      setTemplates(res.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name) {
      toast.error('Template name is required');
      return;
    }

    try {
      setSaving(true);
      await templateAPI.create({
        ...newTemplate,
        default_budget: newTemplate.default_budget ? parseFloat(newTemplate.default_budget) : null,
        default_duration_days: newTemplate.default_duration_days ? parseInt(newTemplate.default_duration_days) : null
      });
      toast.success('Template created');
      setShowCreateDialog(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleUseTemplate = async () => {
    if (!selectedTemplate || !newCampaignName) {
      toast.error('Campaign name is required');
      return;
    }

    try {
      setSaving(true);
      const res = await templateAPI.use(selectedTemplate.id, newCampaignName);
      toast.success('Campaign created from template!');
      setShowUseDialog(false);
      setSelectedTemplate(null);
      setNewCampaignName('');
      // Could redirect to the new campaign
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return;
    try {
      await templateAPI.delete(templateId);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openUseDialog = (template) => {
    setSelectedTemplate(template);
    setNewCampaignName(`${template.name} - ${format(new Date(), 'MMM yyyy')}`);
    setShowUseDialog(true);
  };

  const resetForm = () => {
    setNewTemplate({
      name: '',
      description: '',
      campaign_type: '',
      channels: [],
      target_audience: '',
      default_headline: '',
      default_tagline: '',
      default_cta: '',
      default_body: '',
      default_budget: '',
      default_duration_days: '',
      category: '',
      tags: []
    });
    setTagInput('');
  };

  const toggleChannel = (channel) => {
    const channels = newTemplate.channels.includes(channel)
      ? newTemplate.channels.filter(c => c !== channel)
      : [...newTemplate.channels, channel];
    setNewTemplate({ ...newTemplate, channels });
  };

  const addTag = () => {
    if (tagInput.trim() && !newTemplate.tags.includes(tagInput.trim())) {
      setNewTemplate({ ...newTemplate, tags: [...newTemplate.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  if (loading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaign-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <LayoutTemplate className="h-6 w-6" />
            Campaign Templates
          </h1>
          <p className="text-muted-foreground">
            Reusable templates for faster campaign creation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchTemplates}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <LayoutTemplate className="h-16 w-16 mx-auto text-muted-foreground mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No Templates Yet</h3>
            <p className="text-muted-foreground mb-4">Create your first campaign template</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    {template.category && (
                      <Badge variant="outline" className="mt-1 uppercase">
                        {formatCategory(template.category)}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {hasRole(['admin']) && (
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {template.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {template.description}
                  </p>
                )}

                {/* Template Info */}
                <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                  {template.default_budget && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <DollarSign className="h-3 w-3" />
                      ${template.default_budget.toLocaleString()}
                    </div>
                  )}
                  {template.default_duration_days && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {template.default_duration_days} days
                    </div>
                  )}
                  {template.usage_count > 0 && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Copy className="h-3 w-3" />
                      Used {template.usage_count}x
                    </div>
                  )}
                </div>

                {/* Channels */}
                {template.channels?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {template.channels.slice(0, 4).map(channel => (
                      <Badge key={channel} variant="secondary" className="text-xs">
                        {channel.replace('_', ' ')}
                      </Badge>
                    ))}
                    {template.channels.length > 4 && (
                      <Badge variant="secondary" className="text-xs">
                        +{template.channels.length - 4}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Performance from source */}
                {template.historical_performance && (
                  <div className="p-2 bg-green-50 rounded text-xs text-green-700 mb-3">
                    <TrendingUp className="h-3 w-3 inline mr-1" />
                    Based on successful campaign
                    {template.historical_performance.conversions && 
                      ` • ${template.historical_performance.conversions} conversions`}
                  </div>
                )}

                {/* Action */}
                <Button 
                  className="w-full" 
                  onClick={() => openUseDialog(template)}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Use Template
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Campaign Template</DialogTitle>
            <DialogDescription>
              Create a reusable template for faster campaign creation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="e.g., Q1 Product Launch"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={newTemplate.category} 
                  onValueChange={(v) => setNewTemplate({ ...newTemplate, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newTemplate.description}
                onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                placeholder="When to use this template..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Channels</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg">
                {CHANNELS.map(channel => (
                  <Badge
                    key={channel}
                    variant={newTemplate.channels.includes(channel) ? 'default' : 'outline'}
                    className="cursor-pointer capitalize"
                    onClick={() => toggleChannel(channel)}
                  >
                    {channel.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Target Audience</Label>
              <Input
                value={newTemplate.target_audience}
                onChange={(e) => setNewTemplate({ ...newTemplate, target_audience: e.target.value })}
                placeholder="Who is this campaign for?"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Budget ($)</Label>
                <Input
                  type="number"
                  value={newTemplate.default_budget}
                  onChange={(e) => setNewTemplate({ ...newTemplate, default_budget: e.target.value })}
                  placeholder="10000"
                />
              </div>
              <div className="space-y-2">
                <Label>Default Duration (days)</Label>
                <Input
                  type="number"
                  value={newTemplate.default_duration_days}
                  onChange={(e) => setNewTemplate({ ...newTemplate, default_duration_days: e.target.value })}
                  placeholder="30"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Default Content</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Default Headline</Label>
                  <Input
                    value={newTemplate.default_headline}
                    onChange={(e) => setNewTemplate({ ...newTemplate, default_headline: e.target.value })}
                    placeholder="Main campaign headline"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Tagline</Label>
                    <Input
                      value={newTemplate.default_tagline}
                      onChange={(e) => setNewTemplate({ ...newTemplate, default_tagline: e.target.value })}
                      placeholder="Campaign tagline"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Default CTA</Label>
                    <Input
                      value={newTemplate.default_cta}
                      onChange={(e) => setNewTemplate({ ...newTemplate, default_cta: e.target.value })}
                      placeholder="Learn More"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Default Body</Label>
                  <Textarea
                    value={newTemplate.default_body}
                    onChange={(e) => setNewTemplate({ ...newTemplate, default_body: e.target.value })}
                    placeholder="Default campaign description..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              {newTemplate.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {newTemplate.tags.map(tag => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                      <span 
                        className="ml-1 cursor-pointer" 
                        onClick={() => setNewTemplate({ 
                          ...newTemplate, 
                          tags: newTemplate.tags.filter(t => t !== tag) 
                        })}
                      >×</span>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTemplate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use Template Dialog */}
      <Dialog open={showUseDialog} onOpenChange={setShowUseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Campaign from Template</DialogTitle>
            <DialogDescription>
              {selectedTemplate?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Campaign Name *</Label>
              <Input
                value={newCampaignName}
                onChange={(e) => setNewCampaignName(e.target.value)}
                placeholder="Enter campaign name"
              />
            </div>

            {selectedTemplate && (
              <div className="p-4 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium mb-2">Template includes:</p>
                <ul className="space-y-1 text-muted-foreground">
                  {selectedTemplate.channels?.length > 0 && (
                    <li>• Channels: {selectedTemplate.channels.join(', ')}</li>
                  )}
                  {selectedTemplate.target_audience && (
                    <li>• Target: {selectedTemplate.target_audience}</li>
                  )}
                  {selectedTemplate.default_budget && (
                    <li>• Budget: ${selectedTemplate.default_budget.toLocaleString()}</li>
                  )}
                  {selectedTemplate.default_headline && (
                    <li>• Headline: {selectedTemplate.default_headline}</li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUseDialog(false)}>Cancel</Button>
            <Button onClick={handleUseTemplate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignTemplatesPage;
